import { fileExtension, PDF_EXTENSIONS, PDF_MIME } from "@/lib/file-kinds";
import { localLink } from "@/lib/local-file-link";
import { safeWebUrl } from "@/lib/safe-web-url";
import { ownUploadUrl } from "@/lib/upload-url";

function lowerField(content: Record<string, unknown>, key: string): string {
  const value = content[key];
  return typeof value === "string" ? value.trim().toLowerCase() : "";
}

function contentRecord(content: unknown): Record<string, unknown> | null {
  return content && typeof content === "object" && !Array.isArray(content)
    ? (content as Record<string, unknown>)
    : null;
}

/**
 * True when a file frame's content is a PDF: named by its kind (`pdf`), its
 * `mimeType`, or a `.pdf` name or URL path.
 */
export function isPdfFile(content: unknown): boolean {
  const record = contentRecord(content);
  if (!record) return false;
  const url = safeWebUrl(
    typeof record.url === "string" ? record.url : undefined,
  );
  return (
    lowerField(record, "kind") === "pdf" ||
    lowerField(record, "mimeType") === PDF_MIME ||
    PDF_EXTENSIONS.has(fileExtension(lowerField(record, "name"))) ||
    (url !== null && PDF_EXTENSIONS.has(fileExtension(new URL(url).pathname)))
  );
}

/**
 * Where pdf.js reads a file frame's PDF: a web URL (one of our own uploads,
 * else an https URL) or a file linked on this computer.
 */
export type PdfSource =
  | { kind: "url"; url: string }
  | { kind: "local"; linkId: string; frameId: string };

/**
 * The source pdf.js reads a file frame's PDF from, or null when the file is
 * not a PDF or nothing here may read it: a file linked on this computer,
 * read through frame `linkedHere` (given by the caller when `linkPlace` says
 * here), else one of our own uploads, else an https URL. A file linked on
 * another computer has none. The one predicate the file frame and its card
 * share.
 */
export function pdfSource(
  content: unknown,
  linkedHere: string | null,
): PdfSource | null {
  const record = contentRecord(content);
  if (!record || !isPdfFile(record)) return null;
  const link = localLink(record);
  if (link) {
    return linkedHere
      ? { kind: "local", linkId: link.linkId, frameId: linkedHere }
      : null;
  }
  const own = ownUploadUrl(record.url);
  if (own) return { kind: "url", url: own };
  const url = safeWebUrl(
    typeof record.url === "string" ? record.url : undefined,
  );
  return url?.startsWith("https:") ? { kind: "url", url } : null;
}

/** One string per source, for caches and keys. */
export function pdfSourceKey(source: PdfSource): string {
  return source.kind === "url"
    ? source.url
    : `local:${source.linkId}:${source.frameId}`;
}
